import { Body, Button, Heading, Icon } from '@assembly-js/design-system';
import { Grid } from '@material-ui/core';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { ConfirmationToken as StripeConfirmationToken } from '@stripe/stripe-js';
import { Logger } from 'aws-amplify';
import clsx from 'clsx';
import React from 'react';

import { HOME_PAGE, INVOICES_PAGE } from 'src/constants';
import { PortalConfig, PortalConfigContext } from 'src/context';
import history from 'src/history';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import {
  SignUpData,
  StripePaymentElement,
} from 'src/routes/payments/Checkout/StripePaymentElement/StripePaymentElement';
import {
  Invoice,
  LineItem,
  PaymentOptions,
  PaymentSourceType,
  PaymentStatus,
  selectRecipientInfo,
  updateInvoice,
  usePayInvoiceMutation,
} from 'src/services/api/invoicesApi';
import {
  PayPaymentLinkResponse,
  usePayPaymentLinkMutation,
} from 'src/services/api/paymentLinksApi';
import { useGetProductsQuery } from 'src/services/api/productsApi';
import {
  LineItem as ProductStoreLineItem,
  usePayProductStoreMutation,
} from 'src/services/api/productStoreApi';
import { StripeElementsLayout, StripePaymentMethod } from 'src/types/payment';

export interface CheckoutTarget {
  id: string;
  amountDue: number;
  fees: number;
  tax: number;
  currency: string;
  collectionMethod?: string;
  ignoreFees: boolean;
  customerSessionTargetOverride?: string;
  lineItems: LineItem[];
  isTargetSubscription?: boolean;
  layout: StripeElementsLayout;
  isLoggedInUser?: boolean;
  accountId?: string;
}

type Props<T extends CheckoutTarget> = {
  recipientId: string;
  target: T;
  isActualCheckout: boolean;
  isPreview?: boolean;
  paymentOptions: PaymentOptions;
  selectedSource: StripePaymentMethod | string | null;
  paymentMode?: 'invoice' | 'paymentLink' | 'productStore';
  isLoggedInUser?: boolean;
  setSelectedSource: (
    paymentSource: StripePaymentMethod | string | null,
  ) => void;
  afterCheckoutConfig?: {
    headerText: string;
    descriptionText: string;
    magicLinkSentDescriptionText: string;
    linkText: string;
  };
  leaveCheckoutTargetOverride?: string;
  pressBackButtonTargetOverride?: string;
  ContextSectionComponent: React.ComponentType<{
    target: T;
    onExit: () => void;
    selectedSource?: StripePaymentMethod | string | null;
    isActualCheckout: boolean;
    isAutoChargeSubscription: boolean;
    portalConfig: PortalConfig;
    isPreview?: boolean;
    isLoggedInUser?: boolean;
    onBackArrowClick?: () => void;
  }>;
};

const logger = new Logger('InvoiceCheckout');

export function Checkout<T extends CheckoutTarget>({
  target,
  isActualCheckout = false,
  isPreview,
  paymentOptions,
  selectedSource,
  setSelectedSource,
  recipientId,
  paymentMode = 'invoice',
  ContextSectionComponent,
  isLoggedInUser,
  afterCheckoutConfig,
  leaveCheckoutTargetOverride,
  pressBackButtonTargetOverride,
}: Props<T>) {
  const dispatch = useAppDispatch();
  const recipientInfo = useAppSelector((state) =>
    selectRecipientInfo(state, recipientId),
  );

  useGetProductsQuery(undefined, {
    skip: false,
  });

  const stripe = useStripe();

  const { accountId: accountIdFromPaymentInfo } = useAppSelector(
    (state) => state.payments.paymentInfo,
  );
  const accountId = target.accountId || accountIdFromPaymentInfo;

  const [isPaying, setIsPaying] = React.useState(false);
  const [payInvoice] = usePayInvoiceMutation();
  const [payPaymentLink] = usePayPaymentLinkMutation();
  const [payProductStore] = usePayProductStoreMutation();
  const portalConfig = React.useContext(PortalConfigContext);

  const isPayingInvoice = paymentMode === 'invoice';
  const isPayingPaymentLink = paymentMode === 'paymentLink';
  const isPayingProductStore = paymentMode === 'productStore';
  const isPublicPaymentPossible =
    paymentMode === 'paymentLink' || paymentMode === 'productStore';

  const [
    { isAfterCheckout, redirectUrl, magicLinkSent },
    setPostCheckoutState,
  ] = React.useState<{
    isAfterCheckout: boolean;
    redirectUrl: string | null;
    magicLinkSent: boolean | null;
  }>({
    isAfterCheckout: false,
    redirectUrl: null,
    magicLinkSent: null,
  });

  const payEnabled = React.useMemo(() => {
    if (!selectedSource) {
      return false;
    }

    // This function is only used in our Charge API implementation
    if (
      typeof selectedSource === 'object' &&
      'object' in selectedSource &&
      'status' in selectedSource &&
      selectedSource.object === PaymentSourceType.BankAccount &&
      selectedSource.status !== 'verified'
    ) {
      return false;
    }

    return true;
  }, [selectedSource]);

  const onSubmit = async (
    confirmationToken?: StripeConfirmationToken,
    signUpData?: SignUpData,
  ) => {
    // payEnabled exists effectively to check if the user has a valid payment source
    // The confirmation token does that itself, so in our new flow, we just need to
    // check for the confirmation token
    if (!payEnabled && !confirmationToken) {
      return;
    }

    const totalAmount = target.amountDue + target.fees;
    setIsPaying(true);
    logger.info('Total amount to pay', totalAmount);

    const sourceId =
      selectedSource &&
      typeof selectedSource === 'object' &&
      'id' in selectedSource
        ? selectedSource.id
        : '';

    let result:
      | { data: Invoice }
      | { data: PayPaymentLinkResponse }
      | { error: unknown };
    let returnedInvoice: Invoice | null = null;
    let paymentError: boolean = false;
    try {
      if (isPayingInvoice) {
        result = await payInvoice({
          invoiceId: target.id,
          sourceId,
          manual: false,
          ...(confirmationToken ? { confirmationToken } : {}),
        });
        if ('data' in result) {
          returnedInvoice = result.data;
        } else if ('error' in result) {
          paymentError = true;
        }
      } else if (isPayingPaymentLink) {
        result = await payPaymentLink({
          id: target.id,
          sourceId,
          confirmationToken,
          ...signUpData,
        });
        if ('error' in result) {
          paymentError = true;
        } else if ('data' in result) {
          returnedInvoice = result.data.invoice;
          setPostCheckoutState({
            isAfterCheckout: true,
            redirectUrl: result.data.redirectUrl,
            magicLinkSent: result.data.magicLinkSent,
          });
        }
      } else if (isPayingProductStore) {
        if (!confirmationToken) {
          paymentError = true;
          return;
        }

        const convertedLineItems: ProductStoreLineItem[] = target.lineItems.map(
          (lineItem) => {
            const productStoreLineItem: ProductStoreLineItem = {
              ...lineItem,
              amount: Number(lineItem.amount),
              quantity: Number(lineItem.quantity),
              productId: lineItem.productId || '',
              priceId: lineItem.priceId || '',
              description: lineItem.description || '',
            };
            return productStoreLineItem;
          },
        );

        result = await payProductStore({
          id: target.id,
          confirmationToken,
          lineItems: convertedLineItems,
          ...signUpData,
        });
        if ('error' in result) {
          paymentError = true;
        } else if ('data' in result) {
          returnedInvoice = result.data.invoice;
          setPostCheckoutState({
            isAfterCheckout: true,
            redirectUrl: result.data.redirectUrl,
            magicLinkSent: result.data.magicLinkSent,
          });
        }
      }
      // This is how we handle a payment in the case where someone is logged in
      if (returnedInvoice && !magicLinkSent && !redirectUrl) {
        const { payment: payments } = returnedInvoice;
        // look at the last payment to determine its status
        const lastPayment = payments?.at(-1);
        if (
          stripe &&
          lastPayment?.intent?.status === 'requires_action' &&
          lastPayment.intent.client_secret
        ) {
          // this invoice requires additional action for the payment
          // intent to be confirmed and payment to complete
          const { paymentIntent, error } = await stripe.handleNextAction({
            clientSecret: lastPayment.intent.client_secret,
          });
          if (error) {
            // payment intent was not successful, so show an error
          } else if (returnedInvoice) {
            // the side-effect for invoice being paid is handled in the
            // backend through a webhook received for payment_intent.succeeded
            // on the webapp we can just redirect to the invoices page and
            // mark the payment as processing. When the side-effects have completed
            // and the invoice is updated to paid, the UI will be updated via a wss
            const updatedPayments =
              returnedInvoice.payment?.map((payment) => {
                if (payment.intentId === paymentIntent?.id) {
                  // update the payment status to processing
                  return { ...payment, status: PaymentStatus.Processing };
                }

                return payment;
              }) || null;
            const updatedInvoice = {
              ...returnedInvoice,
              payment: updatedPayments,
            };
            updateInvoice(dispatch, updatedInvoice, recipientInfo);
            handlePaymentCompleted();
          }
        } else {
          handlePaymentCompleted();
        }
      } else if (!paymentError) {
        handlePaymentCompleted();
      }
    } catch (err) {
      logger.error('Error paying invoice', err);
    }
    setIsPaying(false);
  };

  // When paying an invoice, we want to redirect the user to the invoices page.
  // When paying a payment link, we want to redirect the user to the home page.
  let leaveCheckoutTarget: { path: string } = INVOICES_PAGE;
  if (isPublicPaymentPossible) {
    leaveCheckoutTarget = HOME_PAGE;
  }

  const handleBackArrowClick = () => {
    history.push(pressBackButtonTargetOverride ?? leaveCheckoutTarget.path);
  };

  const handleExitCheckout = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      history.push(leaveCheckoutTargetOverride ?? leaveCheckoutTarget.path);
    }
  };

  const handlePaymentCompleted = () => {
    // Do not exit the checkout if we have a custom after checkout config
    if (afterCheckoutConfig) {
      return;
    } else {
      history.push(leaveCheckoutTargetOverride ?? leaveCheckoutTarget.path);
    }
  };

  const isCheckoutVisible = isActualCheckout || isPreview;

  const isTargetSubscription = !!target.isTargetSubscription;
  const isAutoChargeSubscription =
    isTargetSubscription &&
    (target.collectionMethod === 'chargeAutomatically' ||
      isPublicPaymentPossible);

  // TODO[Sal]: I was hoping to move this Elements into the StripePaymentElement component, however,
  // upon trying to do so, I was met with some errors about the Elements component not being found.
  // I want to fix this, but I'd also like to focus on fixing the bug first. Things work just fine
  // here. Will follow up.
  return stripe && accountId !== '' ? (
    <Elements
      stripe={stripe}
      options={{
        fonts: [
          {
            cssSrc: 'https://fonts.googleapis.com/css?family=Inter',
          },
        ],
        // set the default configuration for stripe elements.
        // this is the only way to setup verification method for ACH payment.
        // but if you provide that you need to also provide other options
        // like mode, amount, currency
        mode: 'payment',
        amount: 100, // must be greater than 0, otherwise stripe will throw an error
        currency: 'usd',
        onBehalfOf: accountId,
        paymentMethodOptions: {
          us_bank_account: {
            verification_method: 'instant',
          },
        },
      }}
    >
      {isCheckoutVisible && (
        <Grid
          item
          className={clsx('w-full lg:block', {
            '@4xl:w-1/2': isActualCheckout,
            '@2xl:w-1/2': isPreview,
            '@xs:hidden': afterCheckoutConfig && isAfterCheckout,
          })}
        >
          <div
            className={clsx(
              'pt-4 text-white w-full flex lg:bg-[#F8F9FB]  h-full border-box ',
              {
                '@4xl:pt-[52px] @5xl:justify-end': isActualCheckout,
                '@2xl:pt-[30px] justify-center rounded-bl-lg': isPreview,
              },
            )}
            style={{ boxSizing: 'border-box' }}
          >
            <div
              className={clsx(
                ' flex w-full px-6 @lg:px-[28px] @4xl:pr-[24px] @7xl:px-[80px] @5xl:max-w-[482px]',
              )}
            >
              <ContextSectionComponent
                target={target}
                onExit={handleBackArrowClick}
                selectedSource={selectedSource}
                isActualCheckout={isActualCheckout}
                isAutoChargeSubscription={isAutoChargeSubscription}
                portalConfig={portalConfig}
                isPreview={isPreview}
                isLoggedInUser={isLoggedInUser}
                onBackArrowClick={handleBackArrowClick}
              />
            </div>
          </div>
        </Grid>
      )}
      <Grid
        item
        className={clsx('w-full flex justify-center', {
          'overflow-hidden max-w-full h-[675px]':
            !isCheckoutVisible && !afterCheckoutConfig,
          '@4xl:w-1/2': isActualCheckout,
          '@2xl:w-1/2': isPreview,
          'h-full': !!afterCheckoutConfig && isAfterCheckout,
        })}
      >
        <div
          className={clsx({
            'w-full flex justify-start lg:pr-[28px] lg:pl-[12px] xl:px-[80px] pt-4 lg:pt-[102px]':
              isActualCheckout,
            'pt-4 relative flex flex-col': !isActualCheckout,
            'w-full px-2 @2xl:pt-[80px]': isPreview,
          })}
        >
          {!isCheckoutVisible && (
            <div
              className={clsx('w-full flex', {
                'pt-4 lg:pt-[52px] text-white lg:bg-[#F8F9FB] lg:justify-end':
                  isActualCheckout,
                'pt-4 text-white': !isActualCheckout,
              })}
            >
              <div
                className={clsx('flex ', {
                  'w-full lg:max-w-[482px] px-6 lg:pr-[16px] lg:pl-[28px] xl:px-[80px]':
                    isActualCheckout,
                  'w-full px-6': !isActualCheckout,
                })}
              >
                <ContextSectionComponent
                  target={target}
                  onExit={handleBackArrowClick}
                  selectedSource={selectedSource}
                  isActualCheckout={isActualCheckout}
                  isAutoChargeSubscription={isAutoChargeSubscription}
                  portalConfig={portalConfig}
                />
              </div>
            </div>
          )}
          <div className="w-full @5xl:w-max-[482px] bg-white z-10">
            {afterCheckoutConfig && isAfterCheckout ? (
              <div className="w-full h-full lg:h-[calc(100vh-306px)] flex justify-center items-center">
                <div className="w-[360px] align-middle flex flex-col items-center justify-center">
                  <div className="bg-success-primary rounded-full p-[10px] mb-[22px]">
                    <Icon
                      icon="Check"
                      className="text-white h-[42px] w-[42px]"
                    />
                  </div>
                  <Heading size="xl" className="text-black-heading mb-3">
                    {afterCheckoutConfig.headerText}
                  </Heading>
                  <Body className="text-gray-small text-[12px] mb-4 text-center">
                    {magicLinkSent
                      ? afterCheckoutConfig.magicLinkSentDescriptionText
                      : afterCheckoutConfig.descriptionText}
                  </Body>

                  <Button
                    variant="primary"
                    onClick={handleExitCheckout}
                    label={afterCheckoutConfig.linkText}
                    className="w-full"
                    suffixIcon="ArrowRight"
                  />
                </div>
              </div>
            ) : (
              <StripePaymentElement
                id={target.id}
                customerSessionTarget={
                  target.customerSessionTargetOverride ?? target.id
                }
                onSubmitCallback={onSubmit}
                selectedSource={selectedSource}
                setSelectedSource={setSelectedSource}
                amount={Math.round(target.amountDue + target.fees)}
                currency={target.currency}
                layout={target.layout}
                paymentOptions={paymentOptions}
                onBehalfOf={accountId}
                isPaying={isPaying}
                setIsPaying={setIsPaying}
                isSubscriptionInvoice={isTargetSubscription}
                isAutoChargeSubscription={isAutoChargeSubscription}
                isActualCheckout={isActualCheckout}
                isUnAuthPayer={!isLoggedInUser && isPublicPaymentPossible}
              />
            )}
          </div>
        </div>
      </Grid>
    </Elements>
  ) : null;
}
